import type { PaymentMethod } from "@mountaya/client";
import { z } from "zod";

import type { RestClientConfig } from "../../rest/rules/sdk";
import type { components } from "../../schema/api";
import {
  type OrganizationSubscriptionSummary,
  organizationSubscriptionSummarySchema,
} from "./queries";
import { billingFor } from "./sdk";

type OrganizationBillingPaymentMethodParams =
  components["schemas"]["OrganizationBillingPaymentMethodParams"];
type OrganizationBillingProvisionParams =
  components["schemas"]["OrganizationBillingProvisionParams"];
type SetupIntentSession = components["schemas"]["SetupIntentSession"];

/**
 * organizationSubscriptionSummarySchema (the org's metered Data API subscription and
 * whether a card is on file) is defined in ./queries alongside the single
 * organizationSubscriptionSchema it composes, so the organization read-model and the
 * summary a card attach answers with share one definition without a circular import.
 * Both are re-exported here so the billing surface keeps its established import
 * path (the barrel re-exports them from this module).
 */
export type { OrganizationSubscriptionSummary };
export { organizationSubscriptionSummarySchema };

/**
 * subscriptionSummaryResponseSchema is the REST envelope around the subscription summary a run
 * of the Data API answers with. The inner schema is non-nullable: a run resolves an existing
 * Stripe customer's subscriptions, so the summary is present by construction, and a caller is
 * spared a null branch that cannot happen.
 */
const subscriptionSummaryResponseSchema = z.object({
  data: organizationSubscriptionSummarySchema,
});

/**
 * endedSubscriptionResponseSchema is the REST envelope around what an end of the Data API
 * answers with: the subscription summary, or null when the end found no subscription summary to
 * report, which a caller reads as nothing left running.
 */
const endedSubscriptionResponseSchema = z.object({
  data: organizationSubscriptionSummarySchema.nullable(),
});

/**
 * provisionOrganizationBilling opens a freshly-created organization's billing account and
 * charges nothing: the metered Data API subscription is opened by the first card the
 * organization saves, so the one thing the body carries is an optional promotion code. The
 * success envelope carries nothing the caller reads (the page just navigates into the new org;
 * the card is collected later inline, not via a redirect), so this resolves to void. A non-ok
 * response (the 403 for a non-admin, the 400 for a promotion code refused for this
 * organization) throws a typed error.
 */
export const provisionOrganizationBilling = async (
  config: RestClientConfig,
  organizationId: string,
  params: OrganizationBillingProvisionParams,
): Promise<void> => {
  await billingFor(config).postOrganizationBillingProvision({
    body: { promotion_code: params.promotion_code },
    organization_id: organizationId,
  });
};

/**
 * createOrganizationBillingSetupIntent opens an inline Stripe SetupIntent (`payment-methods/setup`) on the
 * organization's existing Stripe customer and returns its client secret, which
 * Stripe Elements (PaymentElement) consumes in the owner dashboard to confirm a card
 * on the client — the inline alternative to the hosted Customer Portal. It is the
 * first half of the card-on-file flow: the modal fetches the secret on open, the
 * owner enters a card, then confirmOrganizationBillingPaymentMethod attaches it. The
 * secret is single-use and scoped to the org's customer, so it is fetched on demand
 * and never cached. The client types the envelope, so the secret is read off `data`
 * directly. A non-ok response (the 403 for a non-admin, the 409 when the org has no
 * Stripe customer) throws a typed error.
 */
export const createOrganizationBillingSetupIntent = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<SetupIntentSession> => {
  const session = await billingFor(config).postOrganizationBillingPaymentMethodSetup({
    organization_id: organizationId,
  });

  return session.data;
};

/**
 * confirmOrganizationBillingPaymentMethod attaches the card collected by an inline
 * SetupIntent to the organization's Stripe customer and returns the payment method as attached.
 * It is the second half of the card-on-file flow: after Stripe Elements confirms the SetupIntent
 * client-side, the modal posts the succeeded intent's id here, and the backend pins the saved
 * payment method as the customer default, flips the subscriptions to charge automatically, pays
 * the open invoice, and busts the card-on-file gate — synchronously, so the gated action that
 * triggered the modal can retry immediately. What that changed of the organization's account
 * (its subscription summary, what it holds) is the account read's to answer, which the caller
 * invalidates. A non-ok response (the 403 for a non-admin, a 400 when the intent has not
 * succeeded or does not match the org's customer) throws a typed error.
 */
export const confirmOrganizationBillingPaymentMethod = async (
  config: RestClientConfig,
  organizationId: string,
  params: OrganizationBillingPaymentMethodParams,
): Promise<PaymentMethod> => {
  const attached = await billingFor(config).postOrganizationBillingPaymentMethod({
    body: { setup_intent_id: params.setup_intent_id },
    organization_id: organizationId,
  });

  return attached.data;
};

/**
 * endOrganizationBillingSubscription ends the organization's Data API subscription when its
 * current period does, and returns the subscription summary, `cancel_at_period_end` true, or null
 * when there was none left to report. Nothing
 * stops before then: the organization keeps the Data API, and its usage is billed, until
 * `current_period_end_at`. A non-ok response throws a typed error: the 404 when it holds no Data
 * API subscription that has not ended, the 403 for a member who is no owner or admin or an
 * organization billed by agreement, the 503 while another billing change runs.
 */
export const endOrganizationBillingSubscription = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<OrganizationSubscriptionSummary | null> => {
  const answered = await billingFor(config).deleteOrganizationBillingSubscription({
    organization_id: organizationId,
  });

  return endedSubscriptionResponseSchema.parse(answered).data;
};

/**
 * runOrganizationBillingSubscription runs the organization's Data API, whatever state its
 * subscription is in, and returns the subscription summary: it takes back an end scheduled by
 * {@link endOrganizationBillingSubscription}, answers a running subscription as it is, and opens
 * one on the default payment method where none is held (one that has ended, or none ever). A
 * non-ok response throws a typed error: the 402 with no default payment method or no billing
 * account to open one on, a 400 at no field when the payment provider cannot tax the
 * organization or refuses the default payment method, the 403 for a member who is no owner or
 * admin or an organization billed by agreement, the 503 while another billing change runs.
 */
export const runOrganizationBillingSubscription = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<OrganizationSubscriptionSummary> => {
  const answered = await billingFor(config).postOrganizationBillingSubscription({
    organization_id: organizationId,
  });

  return subscriptionSummaryResponseSchema.parse(answered).data;
};
